<template>
  <div class="flex flex-row gap-6 w-full relative items-start">
    <!-- Left Side: Main Templates Dashboard -->
    <div class="flex-1 flex flex-col gap-6 min-w-0">
      <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 class="text-heading-3 text-n-slate-12">Templates</h3>
          <p class="text-n-slate-11">
            View the message templates synced from your WhatsApp inboxes. To create or edit a template, manage it with your provider.
            <a href="https://www.chatwoot.com/docs/channels/whatsapp/whatsapp-templates" target="_blank" rel="noopener noreferrer" class="text-n-blue-9 hover:underline">Know more</a>
          </p>
        </div>
        <div v-if="selectedInbox" class="flex gap-2">
          <next-button
            ghost
            icon="i-lucide-refresh-cw"
            :is-loading="isSyncing"
            label="Sync templates"
            @click="syncTemplates"
          />
          <next-button
            blue
            icon="plus"
            :is-loading="isCreating"
            label="Create Template"
            @click="openModal"
          />
        </div>
      </div>

      <!-- If no WhatsApp inboxes are connected -->
      <div v-if="whatsappInboxes.length === 0" class="p-12 text-center bg-n-surface-1 rounded-xl border border-n-weak flex flex-col items-center gap-2">
        <div>
          <h4 class="text-lg font-semibold text-n-slate-12">No WhatsApp inbox found</h4>
          <p class="text-sm text-n-slate-11 mt-1">To view or sync message templates, please connect a WhatsApp Cloud API inbox first.</p>
        </div>
      </div>

      <!-- Main Dashboard view when inboxes exist -->
      <div v-else class="flex flex-col gap-6">
        <!-- Toolbar Filters ALWAYS VISIBLE -->
        <div class="flex flex-wrap items-center gap-3 p-4 bg-n-surface-1 rounded-xl border border-n-weak">
          <!-- Inbox Selector -->
          <div class="flex flex-col min-w-[200px]">
            <label class="text-xs font-semibold text-n-slate-11 mb-1">WhatsApp Inbox</label>
            <select
              v-model="selectedInboxId"
              class="block w-full text-sm outline outline-1 outline-n-weak rounded-lg bg-n-alpha-black2 p-2"
              @change="onInboxChange"
            >
              <option v-for="inb in whatsappInboxes" :key="inb.id" :value="inb.id">
                {{ inb.name }}
              </option>
            </select>
          </div>

          <!-- Language Selector -->
          <div v-if="availableLanguages.length > 0" class="flex flex-col min-w-[150px]">
            <label class="text-xs font-semibold text-n-slate-11 mb-1">Language</label>
            <select
              v-model="selectedLanguage"
              class="block w-full text-sm outline outline-1 outline-n-weak rounded-lg bg-n-alpha-black2 p-2"
            >
              <option value="ALL">All languages</option>
              <option v-for="lang in availableLanguages" :key="lang" :value="lang">
                {{ lang }}
              </option>
            </select>
          </div>

          <!-- Type Selector -->
          <div v-if="availableCategories.length > 0" class="flex flex-col min-w-[150px]">
            <label class="text-xs font-semibold text-n-slate-11 mb-1">Category</label>
            <select
              v-model="selectedCategory"
              class="block w-full text-sm outline outline-1 outline-n-weak rounded-lg bg-n-alpha-black2 p-2"
            >
              <option value="ALL">All types</option>
              <option v-for="cat in availableCategories" :key="cat" :value="cat">
                {{ cat }}
              </option>
            </select>
          </div>

          <!-- Search Query -->
          <div v-if="templates.length > 0" class="flex flex-col flex-grow min-w-[240px]">
            <label class="text-xs font-semibold text-n-slate-11 mb-1">Search</label>
            <div class="relative flex items-center w-full">
              <span class="absolute z-10 i-lucide-search size-4 text-n-slate-10 pointer-events-none" style="top: 11px; left: 12px;" />
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by content or name..."
                class="block w-full pr-3 py-2 text-sm outline outline-1 outline-n-weak rounded-lg bg-n-alpha-black2 text-n-slate-12 placeholder-n-slate-9"
                style="padding-left: 2.5rem;"
              />
            </div>
          </div>
        </div>

        <div v-if="isLoading" class="flex items-center justify-center py-20">
          <SpinnerLoader :size="36" />
        </div>

        <!-- If selected inbox has no templates -->
        <div v-else-if="templates.length === 0" class="p-16 text-center flex flex-col items-center justify-center gap-3 bg-n-surface-1 rounded-xl border border-n-weak">
          <div class="p-3 bg-n-alpha-2 rounded-2xl text-n-slate-11 mb-1">
            <span class="i-lucide-layout-template size-8 inline-block"></span>
          </div>
          <h4 class="text-base font-semibold text-n-slate-12">No templates found for this inbox</h4>
          <p class="text-sm text-n-slate-11 max-w-md">
            There are no message templates synced for <span class="font-medium text-n-slate-12">{{ selectedInbox ? selectedInbox.name : 'this WhatsApp account' }}</span>.
          </p>
          <div class="flex gap-2 mt-2">
            <next-button
              ghost
              icon="i-lucide-refresh-cw"
              :is-loading="isSyncing"
              label="Sync templates"
              @click="syncTemplates"
            />
            <next-button
              blue
              icon="plus"
              :is-loading="isCreating"
              label="Create Template"
              @click="openModal"
            />
          </div>
        </div>

        <div v-else-if="filteredTemplates.length === 0" class="p-12 text-center bg-n-surface-1 rounded-xl border border-n-weak">
          <p class="text-n-slate-11">No matching templates found.</p>
        </div>

        <!-- Templates List -->
        <div v-else class="flex flex-col gap-3">
          <div
            v-for="template in filteredTemplates"
            :key="template.name"
            class="p-4 bg-n-surface-1 rounded-xl border flex justify-between items-center hover:border-n-slate-5 transition duration-200"
            :class="activePreviewTemplate && activePreviewTemplate.name === template.name ? 'border-n-slate-9 bg-n-slate-2' : 'border-n-weak'"
          >
            <div class="flex items-center gap-3">
              <div class="p-2.5 bg-n-alpha-2 rounded-xl text-n-slate-11">
                <span class="i-lucide-layout-template size-5 inline-block"></span>
              </div>
              <div>
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-medium text-n-slate-12">{{ template.name }}</span>
                  <span
                    class="text-[10px] px-2 py-0.5 rounded-full font-medium"
                    :class="statusBadgeClass(template.status)"
                  >
                    {{ template.status }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-11">
                  {{ formatTemplateCategory(template.category) }} • {{ template.language }}
                </p>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <!-- View details button -->
              <next-button
                ghost
                icon="i-lucide-eye"
                sm
                @click="openPreview(template)"
              />
              <next-button
                ghost
                ruby
                icon="i-lucide-trash"
                sm
                @click="deleteTemplate(template.name)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Template Modal -->
    <Dialog
      v-if="selectedInbox"
      ref="createTemplateDialog"
      title="Create New WhatsApp Template"
      description="Submit a new template for Meta approval."
      confirm-button-label="Submit Template"
      :is-loading="isCreating"
      @confirm="submitTemplate"
    >
      <div class="flex flex-col gap-4 mt-4">
        <FormInput
          v-model="newTemplate.name"
          label="Template Name"
          placeholder="e.g. welcome_message"
          message="Lowercase letters and underscores only"
        />
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">Category</label>
          <select
            v-model="newTemplate.category"
            class="block w-full text-sm outline outline-1 outline-n-weak rounded-lg bg-n-alpha-black2 p-2.5"
          >
            <option value="MARKETING">Marketing</option>
            <option value="UTILITY">Utility</option>
            <option value="AUTHENTICATION">Authentication</option>
          </select>
        </div>
        <FormInput
          v-model="newTemplate.language"
          label="Language Code"
          placeholder="e.g. en_US"
        />
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">Header Type</label>
          <select
            v-model="newTemplate.header_type"
            class="block w-full text-sm outline outline-1 outline-n-weak rounded-lg bg-n-alpha-black2 p-2.5"
          >
            <option value="NONE">None</option>
            <option value="IMAGE">Image</option>
          </select>
        </div>
        <FormInput
          v-if="newTemplate.header_type === 'IMAGE'"
          v-model="newTemplate.header_url"
          label="Example Image URL"
          placeholder="e.g. https://domain.com/sample.jpg"
        />
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">Body Text</label>
          <FormTextArea
            v-model="newTemplate.body"
            placeholder="Enter template message..."
            :rows="4"
          />
        </div>
      </div>
    </Dialog>

    <!-- Right Side: Details / Preview Drawer -->
    <div
      v-if="activePreviewTemplate"
      class="w-96 min-w-[384px] bg-n-surface-1 rounded-xl border border-n-weak p-5 flex flex-col sticky top-6 self-start shadow-xl animate-in slide-in-from-right duration-250"
    >
      <div class="flex justify-between items-start mb-4">
        <div>
          <h4 class="text-base font-semibold text-n-slate-12">{{ activePreviewTemplate.name }}</h4>
          <p class="text-xs text-n-slate-11">Preview how this template appears in WhatsApp.</p>
        </div>
        <next-button
          ghost
          icon="i-lucide-x"
          sm
          class="!p-1.5"
          @click="closePreview"
        />
      </div>

      <!-- Mock Phone Screen Preview -->
      <div class="p-4 bg-n-alpha-black2 rounded-xl border border-n-weak/50 flex flex-col gap-2 mb-6">
        <!-- Message Bubble -->
        <div class="bg-n-surface-2 rounded-xl p-3 shadow-sm border border-n-weak/30 max-w-[85%] self-start relative">
          <!-- Image Header if applicable -->
          <div v-if="hasImageHeader(activePreviewTemplate)" class="rounded-lg overflow-hidden mb-2 bg-n-alpha-3 aspect-video flex items-center justify-center border border-n-weak/20">
            <img
              v-if="getTemplateHeaderUrl(activePreviewTemplate)"
              :src="getTemplateHeaderUrl(activePreviewTemplate)"
              class="object-cover w-full h-full"
              alt="Header Preview"
            />
            <i v-else class="i-lucide-image text-3xl text-n-slate-9"></i>
          </div>

          <!-- Body Text -->
          <p class="text-sm text-n-slate-12 whitespace-pre-wrap leading-relaxed">
            {{ getTemplateBody(activePreviewTemplate) }}
          </p>
        </div>
      </div>

      <!-- Details List -->
      <div class="flex flex-col gap-4 border-t border-n-weak pt-4 mb-6">
        <h5 class="text-xs font-bold text-n-slate-11 uppercase tracking-wider">Template details</h5>
        
        <div class="grid grid-cols-3 text-sm">
          <span class="text-n-slate-10 col-span-1">Status</span>
          <span class="col-span-2">
            <span class="text-xs px-2.5 py-0.5 rounded-full font-medium" :class="statusBadgeClass(activePreviewTemplate.status)">
              {{ activePreviewTemplate.status }}
            </span>
          </span>
        </div>

        <div class="grid grid-cols-3 text-sm">
          <span class="text-n-slate-10 col-span-1">Content type</span>
          <span class="col-span-2 text-n-slate-12 font-medium">
            {{ hasImageHeader(activePreviewTemplate) ? 'Image' : 'Text' }}
          </span>
        </div>

        <div class="grid grid-cols-3 text-sm">
          <span class="text-n-slate-10 col-span-1">Category</span>
          <span class="col-span-2 text-n-slate-12 font-medium">
            {{ formatTemplateCategory(activePreviewTemplate.category) }}
          </span>
        </div>

        <div class="grid grid-cols-3 text-sm">
          <span class="text-n-slate-10 col-span-1">Language</span>
          <span class="col-span-2 text-n-slate-12 font-medium">
            {{ activePreviewTemplate.language }}
          </span>
        </div>

        <div class="grid grid-cols-3 text-sm">
          <span class="text-n-slate-10 col-span-1">Inboxes</span>
          <span class="col-span-2 text-n-slate-12 font-medium">
            {{ selectedInbox ? selectedInbox.name : 'WhatsApp Business' }}
          </span>
        </div>
      </div>

      <!-- Manage in Meta CTA Button -->
      <a
        href="https://business.facebook.com/latest/whatsapp_manager/message_templates"
        target="_blank"
        rel="noopener noreferrer"
        class="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-n-blue-9 hover:bg-n-blue-10 text-white rounded-lg font-medium text-sm transition duration-200"
      >
        <span>Manage in Meta</span>
        <i class="i-lucide-external-link text-base"></i>
      </a>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import FormInput from 'dashboard/components-next/input/Input.vue';
import FormTextArea from 'dashboard/components-next/textarea/TextArea.vue';

export default {
  name: 'WhatsAppTemplatesList',
  components: {
    NextButton,
    SpinnerLoader,
    Dialog,
    FormInput,
    FormTextArea,
  },
  data() {
    return {
      selectedInboxId: null,
      selectedLanguage: 'ALL',
      selectedCategory: 'ALL',
      searchQuery: '',
      templates: [],
      activePreviewTemplate: null,
      isLoading: false,
      isSyncing: false,
      isCreating: false,
      newTemplate: {
        name: '',
        category: 'MARKETING',
        language: 'en_US',
        body: '',
        header_type: 'NONE',
        header_url: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
    }),
    whatsappInboxes() {
      return this.inboxes.filter(
        inb => inb.channel_type === 'Channel::Whatsapp'
      );
    },
    selectedInbox() {
      return this.whatsappInboxes.find(inb => inb.id === this.selectedInboxId);
    },
    hasTemplatesInbox() {
      return !!this.selectedInbox;
    },
    availableLanguages() {
      return Array.from(new Set(this.templates.map(t => t.language))).sort();
    },
    availableCategories() {
      return Array.from(new Set(this.templates.map(t => t.category))).sort();
    },
    filteredTemplates() {
      return this.templates.filter(template => {
        // Search filter
        const matchesSearch =
          template.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          this.getTemplateBody(template)
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase());

        // Language filter
        const matchesLanguage =
          this.selectedLanguage === 'ALL' || template.language === this.selectedLanguage;

        // Category filter
        const matchesCategory =
          this.selectedCategory === 'ALL' || template.category === this.selectedCategory;

        return matchesSearch && matchesLanguage && matchesCategory;
      });
    },
  },
  watch: {
    whatsappInboxes: {
      immediate: true,
      handler(newInboxes) {
        if (newInboxes.length > 0) {
          const exists = newInboxes.some(inb => inb.id === this.selectedInboxId);
          if (!this.selectedInboxId || !exists) {
            this.selectedInboxId = newInboxes[0].id;
            this.fetchTemplates();
          }
        }
      },
    },
  },
  methods: {
    onInboxChange() {
      this.closePreview();
      this.selectedLanguage = 'ALL';
      this.selectedCategory = 'ALL';
      this.searchQuery = '';
      this.templates = [];
      this.fetchTemplates();
    },
    openModal() {
      this.$refs.createTemplateDialog.open();
    },
    closeModal() {
      this.$refs.createTemplateDialog.close();
    },
    openPreview(template) {
      this.activePreviewTemplate = template;
    },
    closePreview() {
      this.activePreviewTemplate = null;
    },
    async fetchTemplates() {
      if (!this.selectedInboxId) return;
      this.isLoading = true;
      try {
        const accountId = this.$route.params.accountId;
        const response = await window.axios.get(
          `/api/v1/accounts/${accountId}/inboxes/${this.selectedInboxId}/whatsapp_templates`
        );
        this.templates = response.data.templates || [];
      } catch (error) {
        useAlert('Failed to fetch WhatsApp templates');
      } finally {
        this.isLoading = false;
      }
    },
    async syncTemplates() {
      if (!this.selectedInboxId) return;
      this.isSyncing = true;
      try {
        const accountId = this.$route.params.accountId;
        await window.axios.get(
          `/api/v1/accounts/${accountId}/inboxes/${this.selectedInboxId}/whatsapp_templates`
        );
        useAlert('Templates synced successfully');
        await this.fetchTemplates();
      } catch (error) {
        useAlert('Failed to sync templates');
      } finally {
        this.isSyncing = false;
      }
    },
    async submitTemplate() {
      if (!this.newTemplate.name || !this.newTemplate.body) {
        useAlert('Please fill in Name and Body');
        return;
      }
      this.isCreating = true;
      try {
        const accountId = this.$route.params.accountId;
        await window.axios.post(
          `/api/v1/accounts/${accountId}/inboxes/${this.selectedInboxId}/whatsapp_templates`,
          { template: this.newTemplate }
        );
        useAlert('Template submitted successfully!');
        this.closeModal();
        this.newTemplate = {
          name: '',
          category: 'MARKETING',
          language: 'en_US',
          body: '',
          header_type: 'NONE',
          header_url: '',
        };
        setTimeout(this.fetchTemplates, 2000);
      } catch (error) {
        useAlert('Failed to create template. Check if name already exists.');
      } finally {
        this.isCreating = false;
      }
    },
    async deleteTemplate(templateName) {
      if (!confirm(`Are you sure you want to delete "${templateName}"?`)) return;

      try {
        const accountId = this.$route.params.accountId;
        await window.axios.delete(
          `/api/v1/accounts/${accountId}/inboxes/${this.selectedInboxId}/whatsapp_templates/${templateName}`
        );
        useAlert('Template deleted');
        if (this.activePreviewTemplate && this.activePreviewTemplate.name === templateName) {
          this.closePreview();
        }
        this.templates = this.templates.filter(t => t.name !== templateName);
      } catch (error) {
        useAlert('Failed to delete template');
      }
    },
    getTemplateBody(template) {
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      return bodyComponent ? bodyComponent.text : 'No body text';
    },
    hasImageHeader(template) {
      const header = template.components?.find(c => c.type === 'HEADER');
      return header && header.format === 'IMAGE';
    },
    getTemplateHeaderUrl(template) {
      const header = template.components?.find(c => c.type === 'HEADER');
      if (header && header.example && header.example.header_url && header.example.header_url.length > 0) {
        return header.example.header_url[0];
      }
      return '';
    },
    formatTemplateCategory(category) {
      if (!category) return '';
      return category.charAt(0) + category.slice(1).toLowerCase();
    },
    statusBadgeClass(status) {
      switch (status) {
        case 'APPROVED':
          return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
        case 'REJECTED':
          return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
        case 'PENDING':
          return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
        default:
          return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      }
    },
  },
};
</script>
